// src/index.tsx
import {
Circle,
ClipPath,
Defs,
Ellipse,
G,
Line,
LinearGradient,
Path,
Polygon,
Polyline,
RadialGradient,
Rect,
Stop,
Svg,
Text,
Tspan,
View
} from "@react-pdf/renderer";
import parse, {domToReact} from "html-react-parser";
import React from "react";
import {renderToStaticMarkup} from "react-dom/server";

// src/constants.ts
var BASE_FONT_SIZE = 11;
var PRESENTATION_ATTRIBUTES = [
  "color",
  "dominantBaseline",
  "fill",
  "fillOpacity",
  "fillRule",
  "opacity",
  "stroke",
  "strokeWidth",
  "strokeOpacity",
  "strokeLinecap",
  "strokeDasharray",
  "transform",
  "textAnchor",
  "visibility"
];

// src/styling.ts
import {StyleSheet} from "@react-pdf/renderer";

// src/utils.ts
var convertUnits = (value) => {
  if (value.endsWith("em")) {
    const [val] = value.split("em");
    return Math.round(Number.parseFloat(val) * BASE_FONT_SIZE);
  }
  return Number.parseFloat(value);
};
var getTspanChildrenOffsets = (node) => {
  const { allDx, allDy } = node.children.reduce((acc, child) => {
    if (child.type === "tag" && child.name === "tspan") {
      if (child.attribs.dx)
        acc.allDx.push(convertUnits(child.attribs.dx));
      if (child.attribs.dy)
        acc.allDy.push(convertUnits(child.attribs.dy));
    }
    return acc;
  }, { allDx: [], allDy: [] });
  if (allDx.length > 1 || allDy.length > 1) {
    console.warn(`<ReactPDFChart /> detected that your chart has <Tspan> nodes nested inside a <Text> node which have different 'dx' or 'dy' attributes. Unfortunately this isn't supported by react-pdf. Text positioning may not be accurate.`);
  }
  return { dx: allDx[0] || 0, dy: allDy[0] || 0 };
};

// src/styling.ts
var styles = StyleSheet.create({
  "recharts-default-legend": {
    justifyContent: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8
  },
  "recharts-legend-item": {
    flexDirection: "row",
    gap: 4
  },
  "recharts-legend-item-text": {
    fontSize: BASE_FONT_SIZE - 1
  },
  "recharts-text": {
    fontSize: BASE_FONT_SIZE
  }
});
var getElementStyle = (attribs, chartStyle, additionalStyle) => {
  const style = [];
  if (attribs.class) {
    for (const className of attribs.class.split(" ")) {
      if (className in styles)
        style.push(styles[className]);
      if (chartStyle && className in chartStyle)
        style.push(chartStyle[className]);
    }
  }
  if ("color" in attribs)
    style.push({ color: attribs.color });
  if ("font-family" in attribs)
    style.push({ fontFamily: attribs["font-family"] });
  if ("font-size" in attribs)
    style.push({ fontSize: attribs["font-size"] });
  if (attribs.style) {
    for (const styleString of attribs.style.split(";")) {
      const [rawKey, value] = styleString.split(":");
      const key = rawKey.toLowerCase();
      if (["backgroundColor", "color", "fill"].includes(key)) {
        style.push({ [key]: value });
      } else if (key === "font-size") {
        style.push({ fontSize: convertUnits(value) });
      } else {
      }
    }
  }
  if (additionalStyle)
    style.push(additionalStyle);
  return style;
};
var getSvgElementStyle = (attribs) => {
  const style = {};
  if (attribs.style) {
    for (const styleString of attribs.style.split(";")) {
      const [rawKey, value] = styleString.split(":");
      const key = rawKey.toLowerCase();
      switch (key) {
        case "color":
        case "fill":
        case "opacity":
        case "stroke":
          style[key] = value;
          break;
        case "stroke-width":
          style.strokeWidth = value;
          break;
        case "stroke-linecap":
          style.strokeLineCap = value;
          break;
        default:
      }
    }
  }
  return style;
};

// src/index.tsx
import {
jsx,
Fragment
} from "react/jsx-runtime";
var renderTextElement = ({
  baseProps,
  chartStyle,
  children,
  node
}) => {
  const { attribs } = node;
  const { dx, dy } = getTspanChildrenOffsets(node);
  let tSpanChildNode;
  const textChildren = React.Children.map(children, (child) => {
    if (!child || typeof child === "string")
      return child;
    if (child.type === "TSPAN") {
      tSpanChildNode = child;
      return child.props.children;
    }
    return child;
  });
  const additionalStyle = tSpanChildNode ? tSpanChildNode.props.style : null;
  return jsx(Text, {
    ...baseProps,
    style: getElementStyle(attribs, chartStyle, additionalStyle),
    x: attribs.x != null ? Number.parseFloat(attribs.x) + dx : dx,
    y: attribs.y != null ? Number.parseFloat(attribs.y) + dy : dy,
    children: textChildren
  });
};
var webSvgToPdfSvg = (children, chartStyle) => {
  const svgString = renderToStaticMarkup(children);
  if (!svgString?.length) {
    throw new Error(`[react-pdf-charts]: <ReactPDFChart /> was unable to convert your chart into static markup. The most common reason for this is that your chart is missing a "height" or "width". If you think it's a different problem, please report this issue.`);
  }
  const htmlReactParserOptions = {
    replace: replaceHtmlWithPdfSvg
  };
  function replaceHtmlWithPdfSvg(node) {
    if (node.type === "text")
      return jsx(Fragment, {
        children: node.data
      });
    if (node.type === "tag") {
      const {
        attribs,
        children: nodeChildren,
        name
      } = node;
      const children2 = domToReact(nodeChildren, htmlReactParserOptions);
      const baseProps = Object.entries(attribs).reduce((acc, [attr, value]) => {
        const [first, ...rest] = attr.split("-");
        const attrName = [
          first,
          ...rest.map((word) => `${word[0].toUpperCase()}${word.slice(1)}`)
        ].join("");
        if (attrName === "strokeDasharray") {
          if (value.includes("px"))
            value = value.replaceAll("px", "");
          if (value.includes(" ")) {
            const [first2, second] = value.split(" ");
            if (first2 !== second) {
              throw new Error(`[react-pdf-charts]: Your chart rendered a (${name}) element with a "${first2}" value that's not supported: ${value}.`);
            }
            value = first2;
          }
        }
        if (PRESENTATION_ATTRIBUTES.includes(attrName)) {
          acc[attrName] = value;
        }
        return acc;
      }, {});
      switch (name.toLowerCase()) {
        case "circle":
          return jsx(Circle, {
            ...baseProps,
            cx: attribs.cx,
            cy: attribs.cy,
            r: attribs.r,
            style: getSvgElementStyle(attribs),
            children: children2
          });
        case "clippath":
          return jsx(ClipPath, {
            ...baseProps,
            children: children2
          });
        case "defs":
          return jsx(Defs, {
            ...baseProps,
            children: children2
          });
        case "desc":
          return jsx(Fragment, {});
        case "div":
          return jsx(View, {
            ...baseProps,
            children: children2
          });
        case "ellipse":
          return jsx(Ellipse, {
            ...baseProps,
            cx: attribs.cx,
            cy: attribs.cy,
            rx: attribs.rx,
            ry: attribs.ry,
            style: getSvgElementStyle(attribs),
            children: children2
          });
        case "g":
          return jsx(G, {
            ...baseProps,
            children: children2
          });
        case "line":
          return jsx(Line, {
            ...baseProps,
            x1: attribs.x1,
            x2: attribs.x2,
            y1: attribs.y1,
            y2: attribs.y2,
            style: getSvgElementStyle(attribs),
            children: children2
          });
        case "li":
          return jsx(View, {
            ...baseProps,
            style: getElementStyle(attribs, chartStyle),
            children: children2
          });
        case "lineargradient":
          return jsx(LinearGradient, {
            ...baseProps,
            id: attribs.id,
            x1: attribs.x1,
            x2: attribs.x2,
            y1: attribs.y1,
            y2: attribs.y2,
            children: children2
          });
        case "path":
          return jsx(Path, {
            ...baseProps,
            d: attribs.d,
            style: getSvgElementStyle(attribs),
            children: children2
          });
        case "polygon":
          return jsx(Polygon, {
            ...baseProps,
            points: attribs.points,
            style: getSvgElementStyle(attribs),
            children: children2
          });
        case "polyline":
          return jsx(Polyline, {
            ...baseProps,
            points: attribs.points,
            style: getSvgElementStyle(attribs),
            children: children2
          });
        case "radialgradient":
          return jsx(RadialGradient, {
            ...baseProps,
            cx: attribs.cx,
            cy: attribs.cy,
            fr: attribs.fr,
            fx: attribs.fx,
            fy: attribs.fy,
            id: attribs.id,
            children: children2
          });
        case "rect":
          return jsx(Rect, {
            ...baseProps,
            height: attribs.height,
            rx: attribs.rx,
            ry: attribs.ry,
            style: getSvgElementStyle(attribs),
            width: attribs.width,
            x: attribs.x,
            y: attribs.y,
            children: children2
          });
        case "span":
          return jsx(Text, {
            ...baseProps,
            style: getElementStyle(attribs, chartStyle),
            children: children2
          });
        case "stop":
          return jsx(Stop, {
            ...baseProps,
            offset: attribs.offset,
            stopColor: attribs.stopColor,
            stopOpacity: attribs.stopOpacity,
            children: children2
          });
        case "svg":
          return jsx(Svg, {
            ...baseProps,
            height: attribs.height,
            style: getElementStyle(attribs, chartStyle),
            viewBox: attribs.viewBox,
            width: attribs.width,
            children: children2
          });
        case "text":
          return renderTextElement({
            baseProps,
            chartStyle,
            children: children2,
            node
          });
        case "title":
          return jsx(Fragment, {});
        case "tspan":
          return jsx(Tspan, {
            ...baseProps,
            style: getElementStyle(attribs, chartStyle),
            children: children2
          });
        case "ul":
          return jsx(View, {
            ...baseProps,
            style: getElementStyle(attribs, chartStyle),
            children: children2
          });
      }
      throw new Error(`[react-pdf-charts]: Your chart rendered a <${name} /> node which isn't supported by <ReactPDFChart /> yet. Please report this issue.`);
    }
    throw new Error(`[react-pdf-charts]: Your chart rendered a node type (${node.type}) which isn't supported by <ReactPDFChart /> yet. Please report this issue.`);
  }
  return parse(svgString, htmlReactParserOptions);
};
var ReactPDFChart = ({ children, chartStyle, debug, style }) => {
  const component = webSvgToPdfSvg(children, chartStyle);
  if (!component || typeof component === "string")
    return jsx(Fragment, {
      children: component
    });
  if (Array.isArray(component)) {
    throw new Error(`[react-pdf-charts]: <ReactPDFChart />'s webSvgToPdfSvg() method returned an array. Which isn't supported at the moment. Please report this issue.`);
  }
  return React.cloneElement(component, { debug, style });
};
var src_default = ReactPDFChart;
export {
  src_default as default
};
